/**
 * 机上調査「解析の設定」パネル (docs/27 §15-6 / §17-4)。
 *
 * - 地区に適用する机上調査テンプレート (`district.aiAnalysis.streetSurveyTemplateId`) を選ぶ (master のみ)。
 * - 地区の上書き (`district.aiAnalysis.streetSurveyOverride` の `extraction` / `result`) を編集する (master のみ)。
 *   空欄の項目はテンプレート (と移行期間の出力フォーマットの既定) に従う。
 * - 実際に AI へ送る方向別プロンプトと選抜プロンプトを、functions と同じ shared-types の組立関数で
 *   プレビューする (C13)。
 * - 現在の設定と違う設定・プロンプトで解析された地点の件数を出す (C12)。
 */
import { Button, useToast } from '@citizen-walk/shared-ui';
import {
  DEFAULT_STREET_SURVEY_KEY,
  diffStreetSurveyExtractionRecord,
  mergeStreetSurveyExtractionDef,
  resolveStreetSurveyAiConfig,
  resolveStreetSurveyDisplay,
  resolveStreetSurveyExtraction,
  type District,
} from '@citizen-walk/shared-types';
import { useMemo, useState } from 'react';
import { saveStreetSurveySettings } from './streetSurveySurveys';
import {
  extractionDefFromDraft,
  extractionDraftFromDef,
  resultFieldsDefFromDraft,
  resultFieldsDraftFromDef,
  StreetSurveyExtractionForm,
  StreetSurveyPromptPreview,
  StreetSurveyResultFieldsForm,
  type StreetSurveyExtractionDraft,
  type StreetSurveyResultFieldsDraft,
} from '../street-survey-settings';
import type { StreetSurveyConfig } from './useStreetSurveyConfig';
import type { StreetSurveyPost } from './useStreetSurveyPosts';
import { useStreetSurveyTemplateOptions } from './useStreetSurveyTemplateOptions';

export interface StreetSurveyExtractionPanelProps {
  districtId: string;
  district: District | null;
  config: StreetSurveyConfig;
  posts: readonly StreetSurveyPost[];
  canEdit: boolean;
  uid: string | null;
  /** 設定する調査 (docs/98 R-97、未指定 = 既定の調査)。 */
  surveyKey?: string;
}

interface PanelDraft {
  templateId: string;
  extraction: StreetSurveyExtractionDraft;
  result: StreetSurveyResultFieldsDraft;
}

export function StreetSurveyExtractionPanel({
  districtId,
  district,
  config,
  posts,
  canEdit,
  uid,
  surveyKey = DEFAULT_STREET_SURVEY_KEY,
}: StreetSurveyExtractionPanelProps) {
  const { toast } = useToast();
  const templates = useStreetSurveyTemplateOptions(true);
  // 選んだ調査の設定 (追加の調査は streetSurveys[] の要素、docs/98 R-97)。
  const surveyAi = resolveStreetSurveyAiConfig(district?.aiAnalysis ?? null, surveyKey);
  const override = surveyAi?.streetSurveyOverride ?? null;
  const storedTemplateId = surveyAi?.streetSurveyTemplateId ?? '';
  const storedKey = JSON.stringify({ k: surveyKey, o: override, t: storedTemplateId });
  const toDraft = (): PanelDraft => ({
    templateId: storedTemplateId,
    extraction: extractionDraftFromDef(override?.extraction),
    result: resultFieldsDraftFromDef(override?.result),
  });
  const [draft, setDraft] = useState<PanelDraft>(toDraft);
  const [draftSourceKey, setDraftSourceKey] = useState(storedKey);
  const [saving, setSaving] = useState(false);
  // 保存値が変わったら (他の画面での保存・読込完了) 下書きを取り直す。effect ではなく描画中に
  // 取り直す (React の「props の変化に合わせて state を調整する」形。余計な再描画を起こさない)。
  if (draftSourceKey !== storedKey) {
    setDraftSourceKey(storedKey);
    setDraft(toDraft());
  }

  // 下書きで選んだテンプレート (保存前でも、引き継ぐ値とプレビューに反映する)。
  const selectedTemplate =
    templates.find((t) => t.id === draft.templateId) ??
    (config.template?.id === draft.templateId ? config.template : null);
  const inherited = useMemo(
    () => resolveStreetSurveyDisplay(config.schema?.streetSurvey, selectedTemplate?.display),
    [config.schema, selectedTemplate],
  );

  const legacy = surveyAi?.legacyModeResults === true;
  const { def, errors } = extractionDefFromDraft(draft.extraction);
  // プレビューは「テンプレート + この下書き」を有効にした場合の内容 (無効のままでも送られる内容を確認できる)。
  const preview = useMemo(
    () =>
      resolveStreetSurveyExtraction(config.schema, {
        extraction: { ...mergeStreetSurveyExtractionDef(inherited.extraction, def), enabled: true },
      }),
    // def は draft から毎回作られるため、draft で判定する。
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config.schema, inherited, draft],
  );

  const current = config.extraction;
  const outdatedCount = useMemo(
    () =>
      current.enabled
        ? posts.filter(
            (p) =>
              p.analysisStatus === 'done' &&
              diffStreetSurveyExtractionRecord(
                p.streetView?.extraction,
                current,
                config.mastersById,
              ).length > 0,
          ).length
        : 0,
    [posts, current, config.mastersById],
  );

  if (!config.schema || preview.candidates.length === 0) return null;

  const missingTemplate = !!storedTemplateId && !config.loading && !config.template;

  async function save() {
    if (!canEdit || !uid || saving) return;
    if (errors.length > 0) {
      toast('error', errors[0]!);
      return;
    }
    setSaving(true);
    try {
      await saveStreetSurveySettings({
        districtId,
        district,
        surveyKey,
        uid,
        patch: {
          templateId: draft.templateId || null,
          override: { extraction: def, result: resultFieldsDefFromDraft(draft.result) },
        },
      });
      toast('success', '机上調査の解析の設定を保存しました。');
    } catch (e) {
      toast('error', `保存に失敗しました: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <details
      className="mb-3 rounded border border-slate-200 bg-white px-3 py-2"
      data-testid="street-survey-extraction-panel"
    >
      <summary className="cursor-pointer text-sm font-semibold text-slate-800">
        解析の設定
        <span className="ml-2 text-xs font-normal text-slate-500">
          {config.template ? `テンプレート: ${config.template.displayName}・` : ''}
          {current.enabled ? '新方式で解析中' : '従来の方式で解析中'}
        </span>
      </summary>

      {outdatedCount > 0 && (
        <p className="mt-2 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-800">
          現在の設定・プロンプトと違う内容で解析された地点が {outdatedCount}{' '}
          件あります。詳細レポートで違いを確認し、必要なら再解析してください。
        </p>
      )}

      <div className="mt-2 grid grid-cols-1 gap-3">
        <p className="text-xs text-slate-600">
          机上調査テンプレートを選ぶと、その設定と文面でこの地区を解析します。ここで入力した項目はこの地区だけに効き、
          空欄の項目はテンプレートに従います。設定を変えると、すでに解析した地点は再解析するまで古い設定の結果のままです。
          新方式では、位置を検出する項目と各件に付ける項目のマスタだけを使い、4
          方向を並行して解析したあと、4
          方向をまとめて見比べて重複を除き、重要な順に並べて地点の説明文を作ります。
        </p>

        <label className="block text-xs text-slate-600 min-w-0">
          机上調査テンプレート
          <select
            className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1 text-sm bg-white"
            style={{ maxWidth: '100%' }}
            value={draft.templateId}
            disabled={!canEdit || saving}
            onChange={(e) => setDraft({ ...draft, templateId: e.target.value })}
          >
            <option value="">テンプレートを使わない</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.displayName}
              </option>
            ))}
            {draft.templateId && !templates.some((t) => t.id === draft.templateId) && (
              <option value={draft.templateId}>
                {config.template?.displayName ?? '見つからないテンプレート'}
              </option>
            )}
          </select>
          {missingTemplate && (
            <span className="mt-0.5 block text-[11px] text-danger-700">
              設定されているテンプレートが見つかりません。解析はテンプレートなしの設定で行われます。
            </span>
          )}
          {selectedTemplate?.description && (
            <span className="mt-0.5 block text-[11px] text-slate-500 whitespace-pre-wrap">
              {selectedTemplate.description}
            </span>
          )}
        </label>

        <p className="rounded border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] text-slate-600">
          新方式で使う解析設定（AI 解析 統合エディタ）の内容は、AI
          モデルと生成パラメータだけです。プロンプトの節・出力の検証ルール・変数の対応・投稿データの項目は使わず、プロンプトはここの設定と文面、出力フォーマットの調査項目とカテゴリマスタから組み立てます。従来の方式では、解析設定のプロンプトをそのまま使います。
        </p>

        {current.problems.length > 0 && (
          <ul className="list-disc pl-5 text-xs text-danger-700">
            {current.problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        )}

        <section className="grid grid-cols-1 gap-2">
          <h4 className="text-xs font-semibold text-slate-700">結果の表示に使う項目</h4>
          <StreetSurveyResultFieldsForm
            schema={config.schema}
            draft={draft.result}
            onChange={(result) => setDraft({ ...draft, result })}
            inheritedDef={inherited.result}
            scope="district"
            disabled={!canEdit || saving}
          />
        </section>

        <section className="grid grid-cols-1 gap-2">
          <h4 className="text-xs font-semibold text-slate-700">解析の設定（新方式）と文面</h4>
          <StreetSurveyExtractionForm
            schema={config.schema}
            draft={draft.extraction}
            onChange={(extraction) => setDraft({ ...draft, extraction })}
            inheritedDef={inherited.extraction}
            scope="district"
            disabled={!canEdit || saving}
            enableBlockedReason={
              legacy
                ? 'この地区は旧方式の解析結果の検証を使っているため、新方式は使えません。'
                : null
            }
          />
        </section>

        {errors.length > 0 && (
          <ul className="list-disc pl-5 text-xs text-danger-700">
            {errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        )}

        {canEdit && (
          <div className="flex justify-end">
            <Button size="sm" onClick={() => void save()} disabled={saving || errors.length > 0}>
              {saving ? '保存中…' : '保存'}
            </Button>
          </div>
        )}

        <StreetSurveyPromptPreview cfg={preview} mastersById={config.mastersById} />
      </div>
    </details>
  );
}
